import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'components/ui/button';
import type { SensorScope } from './contracts';
import type { MigrationOverview, MigrationPlan } from './migrationContracts';
import {
  migrationService,
  useAlertMigration,
  useMigrationActions,
  type MigrationService,
  type ActivateMigration,
  type PrepareMigration,
} from './migrationTransport';
import { alertDate, ErrorNotice, LoadingNotice } from './shared';
import RuleExplanation from './RuleExplanation';
import { ruleModeLabels } from './ruleHelpers';
import { isVersionConflict } from './transport';
import './unifiedAlerts.css';

function ImportedRules({ plan, active }: { plan: MigrationPlan; active: boolean }) {
  return (
    <details open={!active}>
      <summary>
        {active ? 'Vis den overførte opsætning' : 'Gennemgå regler og modtagere'} (
        {plan.ruleMappings.length} regler)
      </summary>
      <div className="ua-stack">
        {active && (
          <p className="ua-muted">
            Dette er opsætningen ved overførslen. Senere ændringer vises under enhedens regler.
          </p>
        )}
        {plan.ruleMappings.map(({ ruleId, desiredRule: rule, recipients }) => (
          <article className="ua-card ua-stack" key={ruleId}>
            <div className="ua-header">
              <h4>{rule.title}</h4>
              <span className="ua-pill">
                {rule.enabled ? ruleModeLabels[rule.mode] : 'Slået fra'}
              </span>
            </div>
            <RuleExplanation rule={rule} recipients={recipients} imported />
            <span className="ua-muted">
              {rule.severity === 'critical' ? 'Kritisk' : 'Advarsel'}
            </span>
          </article>
        ))}
        {plan.ruleMappings.length === 0 && (
          <p>Der er ingen eksisterende alarmregler at overføre for enheden.</p>
        )}
      </div>
    </details>
  );
}

export function MigrationReview({
  scope,
  data,
  refreshing,
  onRefresh,
  onPrepare,
  onActivate,
  showRulesLink = true,
}: {
  scope: SensorScope;
  showRulesLink?: boolean;
  data: MigrationOverview;
  refreshing: boolean;
  onRefresh: () => void;
  onPrepare: (command: PrepareMigration) => Promise<unknown>;
  onActivate: (command: ActivateMigration) => Promise<unknown>;
}) {
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [confirmed, setConfirmed] = useState(false);
  const migration = data.migration;
  const active = migration?.status === 'active';
  const plan = active ? migration : data.plan;
  const prepared = migration?.status === 'prepared';
  const changed = Boolean(
    prepared && plan && plan.sourceFingerprint !== migration.sourceFingerprint
  );
  const reviewChanged = Boolean(
    prepared &&
      plan &&
      (plan.planId !== migration?.planId ||
        JSON.stringify(plan.blockers) !== JSON.stringify(migration?.blockers))
  );
  const blockers = plan?.blockers || [];
  const canPrepare = Boolean(
    plan &&
      !active &&
      (!prepared || changed || reviewChanged) &&
      !blockers.some((item) => item.stage === 'prepare')
  );
  const canActivate = Boolean(
    prepared &&
      plan &&
      !changed &&
      !reviewChanged &&
      blockers.length === 0 &&
      migration?.blockers.length === 0
  );
  const locked = busy || refreshing || confirmed || isVersionConflict(failure);
  async function submit() {
    if (locked || !reviewed || !plan) return;
    setBusy(true);
    setFailure(null);
    try {
      if (canPrepare) await onPrepare({ ...scope, expectedFingerprint: plan.sourceFingerprint });
      else if (canActivate && migration)
        await onActivate({
          ...scope,
          expectedFingerprint: migration.sourceFingerprint,
          expectedVersion: migration.version,
        });
      else return;
      setConfirmed(true);
      setReviewed(false);
    } catch (error) {
      setFailure(error);
    } finally {
      setBusy(false);
    }
  }
  if (active && migration?.origin === 'native') return <section className="ua-stack">
    <h3>Ny enhed med fælles alarmstyring</h3>
    <p>Oprettet {alertDate(migration.activatedAt)}. Enheden starter i Test. Gennemgå rapporteringsfrist, regler og modtagere, før både enhed og regler sættes til Live.</p>
    <p>Der er ingen ældre alarmopsætning at overføre. De gældende regler vises under Indstillinger.</p>
  </section>;
  return (
    <div className="ua-stack">
      <div className="ua-header">
        <h3>{active ? 'Overført alarmopsætning' : 'Flyt alarmstyring hertil'}</h3>
        <span className="ua-pill">
          {active
            ? 'Samlet alarmstyring aktiv'
            : prepared
            ? 'Overført · afventer aktivering'
            : plan
            ? 'Eksisterende alarmstyring'
            : 'Opsætning ikke bekræftet'}
        </span>
      </div>
      {!active && plan && (
        <ol className="ua-migration-steps" aria-label="Trin til samlet alarmstyring">
          <li aria-current={!prepared || changed || reviewChanged ? 'step' : undefined}>
            <strong>1. Gennemgå og overfør</strong>
            <span>Gem en kopi. De eksisterende alarmer fortsætter.</span>
          </li>
          <li aria-current={prepared && !changed && !reviewChanged ? 'step' : undefined}>
            <strong>2. Aktivér</strong>
            <span>Skift alarmstyring for denne ene enhed.</span>
          </li>
        </ol>
      )}
      {active ? (
        <p>
          Enheden bruger den samlede alarmstyring. Regler, driftsform og opfølgning styres her.
          Aktiveret {alertDate(migration.activatedAt)}.
        </p>
      ) : plan || migration ? (
        <p>
          Enhedens eksisterende alarmer fortsætter, indtil den overførte opsætning aktiveres. Test
          og Ikke i brug i den fælles visning stopper derfor endnu ikke de eksisterende beskeder.
        </p>
      ) : (
        <p>
          Alarmopsætningen kunne ikke bekræftes. Hent den seneste opsætning, før du ændrer
          alarmstyringen.
        </p>
      )}
      {!active && plan && (
        <section className="ua-stack" aria-label="Før og efter aktivering">
          <h4>Hvad ændrer sig, når du aktiverer?</h4>
          <div className="ua-migration-steps">
            <div className="ua-card">
              <h4>Nu · eksisterende alarmer</h4>
              <p>
                De eksisterende systemer sender fortsat enhedens alarmer, også efter “Overfør
                opsætning”.
              </p>
              <p>
                Hyppighed og afslutning afhænger af den gamle alarmtype. De gamle værdier vises ikke
                som en komplet sammenligning her.
              </p>
            </div>
            <div className="ua-card">
              <h4>Efter · samlet alarmstyring</h4>
              <p>
                Efter “Aktivér samlet alarmstyring” overtager reglerne nedenfor de omfattede alarmer
                for denne enhed.
              </p>
              <p>
                Kontrollér især modtagere, påmindelser og afslutning. Test og pause sender ingen
                driftsbeskeder.
              </p>
            </div>
          </div>
          <p className="ua-muted">
            Andre enheder og særskilte rapporter fortsætter som før. Et allerede kendt problem
            sender ikke en ny åbningsbesked alene på grund af overtagelsen.
          </p>
        </section>
      )}
      {prepared && !active && (
        <p>
          Opsætningen er overført. Kontrollér regler og modtagere, før den samlede alarmstyring
          overtager.
        </p>
      )}
      {changed && (
        <p className="ua-notice ua-warning" role="alert">
          Den eksisterende opsætning er ændret siden overførslen. Gennemgå og overfør den nye
          opsætning, før du aktiverer.
        </p>
      )}
      {reviewChanged && !changed && (
        <p className="ua-notice ua-warning" role="alert">
          Kontrollen af opsætningen er ændret. Gennemgå resultatet, og overfør opsætningen igen før
          aktivering.
        </p>
      )}
      {plan && (
        <p className="ua-muted">
          Modtagere overføres fra den eksisterende opsætning og vises kun til gennemsyn.
          Modtagerlisten kan endnu ikke redigeres her; alarmreglernes beskedkanaler kan ændres.
        </p>
      )}
      {plan ? (
        <ImportedRules plan={plan} active={Boolean(active)} />
      ) : (
        !active && (
          <p>Der er ingen opsætning klar til overførsel. Opdatér for at kontrollere igen.</p>
        )
      )}
      {blockers.length > 0 && (
        <div className="ua-notice ua-warning" role="alert">
          <strong>Dette skal afklares, før alarmstyringen kan overtages</strong>
          <ul>
            {blockers.map((blocker, index) => (
              <li key={`${blocker.code}:${index}`}>{blocker.message}</li>
            ))}
          </ul>
        </div>
      )}
      {plan && plan.notes.length > 0 && (
        <details open={!active}>
          <summary>Vigtigt ved overtagelsen</summary>
          <ul>
            {plan.notes.map((note, index) => (
              <li key={index}>{note}</li>
            ))}
          </ul>
        </details>
      )}
      {failure != null && <ErrorNotice error={failure} />}
      {confirmed && <p role="status">Ændringen er bekræftet. Henter den opdaterede opsætning…</p>}
      {(canPrepare || canActivate) && (
        <>
          <label className="ua-check">
            <input
              type="checkbox"
              checked={reviewed}
              disabled={locked}
              onChange={(event) => setReviewed(event.target.checked)}
            />
            Jeg har gennemgået regler og modtagere for denne enhed
          </label>
          <Button type="button" disabled={locked || !reviewed} onClick={() => void submit()}>
            {busy ? 'Gemmer…' : canPrepare ? 'Overfør opsætning' : 'Aktivér samlet alarmstyring'}
          </Button>
        </>
      )}
      <div className="ua-actions">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy || refreshing}
          onClick={onRefresh}
        >
          {refreshing ? 'Henter opsætning…' : 'Hent seneste opsætning'}
        </Button>
        {showRulesLink && (
          <Link className="ua-link" to={`/monitoring/unified/rules?${new URLSearchParams(scope)}`}>
            Indstillinger for enheden
          </Link>
        )}
      </div>
    </div>
  );
}

export default function MigrationStatusPanel({
  orgId,
  sensorId,
  service = migrationService,
  showRulesLink = true,
}: SensorScope & { service?: MigrationService; showRulesLink?: boolean }) {
  const scope = { orgId, sensorId };
  const query = useAlertMigration(scope, service);
  const actions = useMigrationActions(service);
  const [reload, setReload] = useState(0);
  return (
    <section className="unified-alerts ua-card ua-stack" aria-label="Enhedens alarmopsætning">
      {query.isPending && <LoadingNotice>Henter alarmopsætning…</LoadingNotice>}
      <ErrorNotice
        error={query.isError ? query.error : null}
        onRetry={() => void query.refetch()}
      />
      {query.data && !query.isError && (
        <MigrationReview
          key={`${orgId}:${sensorId}:${query.data.plan?.planId}:${query.data.plan?.sourceFingerprint}:${query.data.migration?.version}:${reload}`}
          scope={scope}
          showRulesLink={showRulesLink}
          data={query.data}
          refreshing={query.isFetching}
          onPrepare={actions.prepare}
          onActivate={actions.activate}
          onRefresh={() => {
            void query.refetch().then((result) => {
              if (!result.isError) setReload((value) => value + 1);
            });
          }}
        />
      )}
    </section>
  );
}
